// src/pages/CapsuleViewer.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AlphaTabApi } from '@coderline/alphatab';
import { AlphaTabViewer } from '../components/AlphaTabViewer';
import Header from '../components/Header';
import { IntroView } from '../components/IntroView';
import { VideoPlayer, VideoPlayerRef, ZoomPreset } from '../components/VideoPlayer';
import { RecordingOverlay } from '../components/RecordingOverlay';
import { LayerTabs } from '../components/LayerTabs';
import { useSyncedPlayback } from '../hooks/useSyncedPlayback';
import { useSyncedVideoPlayback } from '../hooks/useSyncedVideoPlayback';
import { useMetronome } from '../hooks/useMetronome';
import { useRecorder } from '../hooks/useRecorder';
import {
  LayerName,
  getAvailableLayers,
  getLayerFilePath,
  getLayerAudioPath,
  getLayerVideoPath,
  layerHasVideo,
} from '../types/capsule';
import { mergeMusicXmlParts } from '../utils/musicXmlMerger';
import { extractBpmFromFilename } from '../utils/bpmExtractor';
import { useCapsule } from '../features/capsules/hooks/useCapsule';
import { CapsuleVideoControls } from '../features/capsules/components/CapsuleVideoControls';
import { CapsuleAudioControls } from '../features/capsules/components/CapsuleAudioControls';
import { uploadRecording } from '../lib/uploadRecording';
import { useSubmissions } from '../hooks/useSubmissions';
import '../styles/CapsuleViewer.css';

export function CapsuleViewer() {
  const { slug: capsuleSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedLayer, setSelectedLayer] = useState<LayerName | null>(null);
  const [alphaTabApi, setAlphaTabApi] = useState<AlphaTabApi | null>(null);
  const [processedScoreData, setProcessedScoreData] = useState<Uint8Array | null>(null);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const [currentZoom, setCurrentZoom] = useState<ZoomPreset>('full');
  const videoPlayerRef = useRef<VideoPlayerRef>(null);
  const scoreContainerRef = useRef<HTMLElement | null>(null);

  // Score display toggle (shows both notation and tab when enabled)
  const [showScore, setShowScore] = useState(true);

  // Overlay mode: AlphaTab overlays bottom of video instead of stacking below
  const [overlayMode, setOverlayMode] = useState(() => {
    try {
      return localStorage.getItem('fingerstyle-alphatab-overlay') === 'true';
    } catch { return false; }
  });

  // Read ?overlay=true/false from URL params and persist to localStorage
  useEffect(() => {
    const overlayParam = searchParams.get('overlay');
    if (overlayParam === 'true') {
      setOverlayMode(true);
      try { localStorage.setItem('fingerstyle-alphatab-overlay', 'true'); } catch { /* ignore */ }
    } else if (overlayParam === 'false') {
      setOverlayMode(false);
      try { localStorage.removeItem('fingerstyle-alphatab-overlay'); } catch { /* ignore */ }
    }
  }, [searchParams]);

  // BPM tracking for playback speed correction
  const [scoreTempo, setScoreTempo] = useState<number | null>(null);
  const [mediaBpm, setMediaBpm] = useState<number | null>(null);

  // Metronome state
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [metronomeMuted, setMetronomeMuted] = useState(false);

  // User-selected tempo in BPM (initialized to score tempo when loaded)
  const [currentTempo, setCurrentTempo] = useState<number>(120);

  // Video volume state (persisted in VideoPlayer via localStorage)
  const [videoVolume, setVideoVolume] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('fingerstyle-video-volume');
      if (saved !== null) {
        const val = parseFloat(saved);
        if (!isNaN(val) && val >= 0 && val <= 1) return val;
      }
    } catch { /* ignore */ }
    return 1;
  });

  // Recording state
  const [recordingMode, setRecordingMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const volumeBeforeRecordingRef = useRef<number>(1);
  const {
    isRecording,
    startRecording,
    stopRecording,
    recordingBlob,
    recordingUrl: recordingAudioUrl,
    clearRecording,
    error: recorderError,
    stream: recorderStream,
    hasVideo: recorderHasVideo,
  } = useRecorder();
  const { submitRecording } = useSubmissions();

  // Load capsule from URL params
  const { capsule, loading, error } = useCapsule(capsuleSlug ?? null);

  useEffect(() => {
    if (!capsule) {
      setSelectedLayer(null);
      return;
    }
    const layers = getAvailableLayers(capsule);
    if (layers.length === 0) {
      setSelectedLayer(null);
      return;
    }
    setSelectedLayer((prev) => (prev && layers.includes(prev) ? prev : layers[0]));
  }, [capsule]);

  // Handle ?record=true&layer=xxx query params (re-record flow from submission history)
  useEffect(() => {
    if (!capsule) return;

    const shouldRecord = searchParams.get('record') === 'true';
    const layerParam = searchParams.get('layer') as LayerName | null;

    if (!shouldRecord) return;

    const layers = getAvailableLayers(capsule);

    if (layerParam && layers.includes(layerParam)) {
      setSelectedLayer(layerParam);
    }

    setRecordingMode(true);

    // Clear the record param so it doesn't persist on refresh
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('record');
    setSearchParams(newParams, { replace: true });
  }, [capsule, searchParams, setSearchParams]);

  // Get current layer content
  const scoreUrl = capsule && selectedLayer
    ? getLayerFilePath(capsule, selectedLayer)
    : null;

  // Get audio URL and offset from capsule
  const audioUrl = capsule && selectedLayer
    ? getLayerAudioPath(capsule, selectedLayer)
    : null;

  const audioOffset = capsule && selectedLayer
    ? capsule.layers[selectedLayer]?.audioOffset ?? 0
    : 0;

  // Determine if current layer has video
  const hasVideo = capsule && selectedLayer
    ? layerHasVideo(capsule, selectedLayer)
    : false;

  const videoUrl = capsule && selectedLayer && hasVideo
    ? getLayerVideoPath(capsule, selectedLayer)
    : null;

  const videoOffset = capsule && selectedLayer && hasVideo
    ? capsule.layers[selectedLayer]?.videoOffset ?? 0
    : 0;

  const zoomRegions = capsule && selectedLayer && hasVideo
    ? capsule.layers[selectedLayer]?.zoomRegions
    : undefined;

  // Fetch and merge MusicXML for notation + TAB display
  useEffect(() => {
    if (!scoreUrl) {
      setProcessedScoreData(null);
      return;
    }

    // Check if MusicXML (strip query params for extension check)
    const urlWithoutParams = scoreUrl.split('?')[0].toLowerCase();
    const isMusicXml = urlWithoutParams.endsWith('.xml') ||
                       urlWithoutParams.endsWith('.musicxml');

    if (!isMusicXml) {
      // Not MusicXML, let AlphaTab load from URL directly
      setProcessedScoreData(null);
      return;
    }

    // Fetch and merge MusicXML
    fetch(scoreUrl)
      .then(r => r.text())
      .then(xmlText => {
        const mergeResult = mergeMusicXmlParts(xmlText);
        const encoder = new TextEncoder();
        const bytes = encoder.encode(mergeResult.xmlContent);
        setProcessedScoreData(new Uint8Array(bytes));
      })
      .catch(() => {
        setProcessedScoreData(null);
      });
  }, [scoreUrl]);

  // Use the synced playback hook for audio
  const {
    isReady: audioReady,
    isPlaying: audioPlaying,
    offset,
    play: playAudio,
    pause: pauseAudio,
    stop: stopAudio,
  } = useSyncedPlayback({
    alphaTabApi,
    audioUrl,
    initialOffset: audioOffset,
  });

  // Calculate baseSpeed for BPM correction (mediaBpm / scoreTempo)
  // This adjusts video playback to keep it synced with AlphaTab cursor
  const baseSpeed = (mediaBpm && scoreTempo && scoreTempo > 0)
    ? mediaBpm / scoreTempo
    : 1.0;

  // Use the video sync hook (conditionally)
  const {
    isReady: videoReady,
    isPlaying: videoPlaying,
    offset: videoSyncOffset,
    play: playVideo,
    pause: pauseVideo,
    stop: stopVideo,
  } = useSyncedVideoPlayback({
    alphaTabApi: hasVideo ? alphaTabApi : null,
    videoElement: hasVideo ? videoElement : null,
    initialOffset: videoOffset,
    baseSpeed: hasVideo ? baseSpeed : 1.0,
    scoreTempo: scoreTempo || 120,
    currentTempo,
  });

  // Metronome hook
  const {
    isBeatActive,
    isDownbeatActive,
  } = useMetronome({
    alphaTabApi,
    enabled: metronomeEnabled,
    muted: metronomeMuted,
    currentTempo,
  });

  // Sync currentTempo with scoreTempo when score loads
  useEffect(() => {
    if (scoreTempo && scoreTempo > 0) {
      setCurrentTempo(scoreTempo);
    }
  }, [scoreTempo]);

  // Wrap stop functions to also scroll view back to start
  const handleStopAudio = useCallback(() => {
    stopAudio();
    if (scoreContainerRef.current) {
      scoreContainerRef.current.scrollLeft = 0;
    }
  }, [stopAudio]);

  const handleStopVideo = useCallback(() => {
    stopVideo();
    if (scoreContainerRef.current) {
      scoreContainerRef.current.scrollLeft = 0;
    }
  }, [stopVideo]);

  // Volume change handler - syncs state and persists to localStorage
  const handleVolumeChange = useCallback((vol: number) => {
    setVideoVolume(vol);
    videoPlayerRef.current?.setVolume(vol);
    try {
      localStorage.setItem('fingerstyle-video-volume', String(vol));
    } catch { /* ignore */ }
  }, []);

  // Recording submit handler - uploads to Cloudinary then creates submission
  const handleRecordingSubmit = useCallback(async (blob: Blob) => {
    if (!capsule || !selectedLayer) return;
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const result = await uploadRecording(blob, capsule.slug, (progress) => {
        setUploadProgress(progress.percent);
      });
      // Create submission record with the Cloudinary URL
      await submitRecording(capsule.slug, selectedLayer, result.secureUrl);
      // Upload + submission succeeded - close recording mode
      clearRecording();
      setRecordingMode(false);
    } catch {
      // Error is visible via upload state; user can retry
    } finally {
      setIsUploading(false);
    }
  }, [capsule, selectedLayer, clearRecording, submitRecording]);

  // Close recording mode — also stops playback and restores volume
  const handleCloseRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
      // Stop playback that was started with recording
      if (hasVideo) {
        pauseVideo();
      } else {
        pauseAudio();
      }
      // Restore volume
      handleVolumeChange(volumeBeforeRecordingRef.current);
    }
    clearRecording();
    setRecordingMode(false);
  }, [isRecording, stopRecording, clearRecording, hasVideo, pauseVideo, pauseAudio, handleVolumeChange]);

  // Start recording with synchronized video playback and lowered volume
  const RECORDING_VOLUME = 0.15;
  const handleStartRecordingWithPlayback = useCallback(async () => {
    // Save current volume and lower it to avoid mic bleed
    volumeBeforeRecordingRef.current = videoVolume;
    handleVolumeChange(RECORDING_VOLUME);
    // Start mic capture
    await startRecording();
    // Start video + AlphaTab playback
    if (hasVideo && videoReady) {
      playVideo();
    } else if (audioReady) {
      playAudio();
    }
  }, [videoVolume, handleVolumeChange, startRecording, hasVideo, videoReady, playVideo, audioReady, playAudio]);

  // Stop recording and restore video state
  const handleStopRecordingWithPlayback = useCallback(() => {
    stopRecording();
    // Stop video/audio playback
    if (hasVideo) {
      pauseVideo();
    } else {
      pauseAudio();
    }
    // Restore volume
    handleVolumeChange(volumeBeforeRecordingRef.current);
  }, [stopRecording, hasVideo, pauseVideo, pauseAudio, handleVolumeChange]);

  // Keyboard shortcuts: P = play/pause, O = stop
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore when typing in input fields
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      const key = e.key.toLowerCase();

      // Use video controls if video mode, otherwise audio controls
      const isReady = hasVideo ? videoReady : audioReady;
      const isPlaying = hasVideo ? videoPlaying : audioPlaying;
      const play = hasVideo ? playVideo : playAudio;
      const pause = hasVideo ? pauseVideo : pauseAudio;
      const stop = hasVideo ? handleStopVideo : handleStopAudio;

      if ((key === 'p' || key === ' ') && isReady) {
        e.preventDefault();
        if (isPlaying) {
          pause();
        } else {
          play();
        }
      } else if (key === 'o' && isReady) {
        e.preventDefault();
        stop();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasVideo, videoReady, videoPlaying, playVideo, pauseVideo, handleStopVideo, audioReady, audioPlaying, playAudio, pauseAudio, handleStopAudio]);

  // Handle AlphaTab ready - store API reference
  const handleAlphaTabReady = useCallback((api: AlphaTabApi) => {
    setAlphaTabApi(api);
  }, []);

  // Handle score loaded - extract tempo (this is when api.score is actually available)
  const handleScoreLoaded = useCallback((score: { tempo: number }) => {
    if (score && score.tempo > 0) {
      setScoreTempo(score.tempo);
    }
  }, []);

  // Extract media BPM from audio/video filename when layer changes
  useEffect(() => {
    if (!capsule || !selectedLayer) {
      setMediaBpm(null);
      return;
    }

    const layer = capsule.layers[selectedLayer];
    if (!layer) {
      setMediaBpm(null);
      return;
    }

    // Try video first, then audio
    const mediaFilename = layer.video || layer.audio;
    if (mediaFilename) {
      const extractedBpm = extractBpmFromFilename(mediaFilename);
      if (extractedBpm) {
        setMediaBpm(extractedBpm);
      } else {
        setMediaBpm(null);
      }
    } else {
      setMediaBpm(null);
    }
  }, [capsule, selectedLayer]);

  // Whether the intro layer is currently selected
  const isIntroLayer = selectedLayer === 'intro';

  // Callback for IntroView "Start Learning" button
  const handleStartLearning = useCallback(() => {
    if (!capsule) return;
    const layers = getAvailableLayers(capsule);
    const firstNonIntro = layers.find(l => l !== 'intro');
    if (firstNonIntro) {
      setSelectedLayer(firstNonIntro);
    }
  }, [capsule]);

  // Build media indicator map (audio or video)
  const hasMedia = capsule
    ? Object.fromEntries(
        getAvailableLayers(capsule).map(layer => [
          layer,
          !!(capsule.layers[layer]?.audio || capsule.layers[layer]?.video)
        ])
      ) as Partial<Record<LayerName, boolean>>
    : {};

  if (loading) {
    return (
      <div className="capsule-viewer">
        <Header />
        <div className="capsule-viewer-status">
          Loading capsule...
        </div>
      </div>
    );
  }

  if (error || !capsule || !selectedLayer) {
    return (
      <div className="capsule-viewer">
        <Header />
        <div className="capsule-viewer-status capsule-viewer-status--error">
          {error || 'Unable to load capsule'}
        </div>
        <div className="capsule-viewer-error-actions">
          <button className="btn btn-secondary" onClick={() => window.location.reload()}>
            Try Again
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Responsive layout for video mode
  // Use calc() to give AlphaTab a fixed minimum while video takes remaining space
  // Header area is approximately 140px (header 56 + title row 48 + controls 36)
  // AlphaTab needs at least 280px to show notation + tablature fully with nice spacing
  const headerAreaPx = 140;
  const alphaTabMinPx = 280;
  // If score is hidden, video takes full remaining height
  const showAlphaTab = showScore;
  // In overlay mode, video takes full remaining height (AlphaTab overlays on top)
  const isOverlayActive = overlayMode && hasVideo && showAlphaTab;
  const videoHeight = (showAlphaTab && !isOverlayActive)
    ? `calc(100vh - ${headerAreaPx}px - ${alphaTabMinPx}px)`
    : `calc(100vh - ${headerAreaPx}px)`;

  const capsuleStyle = {
    '--capsule-video-height': videoHeight,
    '--capsule-alphatab-min': `${alphaTabMinPx}px`,
  } as CSSProperties;

  return (
    <div
      className={`capsule-viewer ${hasVideo ? 'capsule-viewer--video' : 'capsule-viewer--audio'}`}
      style={capsuleStyle}
    >
      <Header />

      <div className="capsule-layout">
        {/* Left sidebar with vertical layer tabs */}
        <aside className="capsule-sidebar">
          <div className="capsule-sidebar-title">
            <h1 className="capsule-title">{capsule.metadata.title}</h1>
            {capsule.metadata.artist && (
              <p className="capsule-artist">{capsule.metadata.artist}</p>
            )}
          </div>
          <LayerTabs
            availableLayers={getAvailableLayers(capsule)}
            selectedLayer={selectedLayer}
            onSelectLayer={setSelectedLayer}
            hasAudio={hasMedia}
            showAllLayers={true}
            orientation="vertical"
          />
        </aside>

        {/* Main content area */}
        <main className="capsule-main">
          {/* Intro layer view */}
          {isIntroLayer && (
            <IntroView capsule={capsule} onStartLearning={handleStartLearning} />
          )}

          {!isIntroLayer && hasVideo && (
            <CapsuleVideoControls
              videoPlaying={videoPlaying}
              videoReady={videoReady}
              onPlay={playVideo}
              onPause={pauseVideo}
              onStop={handleStopVideo}
              isBeatActive={isBeatActive}
              isDownbeatActive={isDownbeatActive}
              metronomeEnabled={metronomeEnabled}
              metronomeMuted={metronomeMuted}
              onToggleMetronome={() => setMetronomeEnabled(!metronomeEnabled)}
              onToggleMute={() => setMetronomeMuted(!metronomeMuted)}
              currentTempo={currentTempo}
              scoreTempo={scoreTempo || 120}
              onTempoChange={setCurrentTempo}
              zoomRegions={zoomRegions}
              currentZoom={currentZoom}
              onZoomFull={() => {
                videoPlayerRef.current?.setZoom('full');
                setCurrentZoom('full');
              }}
              onZoomRight={() => {
                videoPlayerRef.current?.setZoom('rightHand');
                setCurrentZoom('rightHand');
              }}
              onZoomLeft={() => {
                videoPlayerRef.current?.setZoom('leftHand');
                setCurrentZoom('leftHand');
              }}
              showScore={showScore}
              onToggleScore={() => setShowScore(!showScore)}
              videoSyncOffset={videoSyncOffset}
              volume={videoVolume}
              onVolumeChange={handleVolumeChange}
            />
          )}

          {/* Record button (on non-intro layers, when not already in recording mode) */}
          {!isIntroLayer && !recordingMode && (
            <div className="capsule-controls" style={{ paddingTop: 0, borderBottom: 'none' }}>
              <button
                onClick={() => setRecordingMode(true)}
                className="capsule-btn capsule-btn--record"
              >
                Record My Performance
              </button>
            </div>
          )}

          {/* Recording overlay (when recording mode active) */}
          {!isIntroLayer && recordingMode && (
            <RecordingOverlay
              isRecording={isRecording}
              recordingUrl={recordingAudioUrl}
              recordingBlob={recordingBlob}
              error={recorderError}
              onStartRecording={handleStartRecordingWithPlayback}
              onStopRecording={handleStopRecordingWithPlayback}
              onClearRecording={clearRecording}
              onSubmit={handleRecordingSubmit}
              onClose={handleCloseRecording}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              stream={recorderStream}
              hasVideo={recorderHasVideo}
            />
          )}

          {/* Video + AlphaTab: overlay wrapper when overlay active, separate divs otherwise */}
          {!isIntroLayer && hasVideo && videoUrl && isOverlayActive && (
            <div className="capsule-video-overlay-wrapper">
              <div className="capsule-video">
                <VideoPlayer
                  ref={videoPlayerRef}
                  src={videoUrl}
                  zoomRegions={zoomRegions}
                  onVideoReady={setVideoElement}
                  onVolumeChange={setVideoVolume}
                />
              </div>
              <div
                ref={(el) => {
                  if (el) {
                    const alphaTabContainer = el.querySelector('[data-testid="notation-container"]');
                    scoreContainerRef.current = alphaTabContainer as HTMLElement || el;
                  }
                }}
                className="capsule-score capsule-score--overlay"
              >
                {(scoreUrl || processedScoreData) && (
                  <AlphaTabViewer
                    key={`${capsule.slug}-${selectedLayer}`}
                    scoreUrl={processedScoreData ? undefined : (scoreUrl || undefined)}
                    scoreData={processedScoreData || undefined}
                    onReady={handleAlphaTabReady}
                    onScoreLoaded={handleScoreLoaded}
                    hidePlaybackControls={!!(audioUrl || hasVideo)}
                    muteAudio={!!(audioUrl || hasVideo)}
                    layoutMode="horizontal"
                  />
                )}
              </div>
            </div>
          )}

          {/* Video section (when video mode, non-overlay) */}
          {!isIntroLayer && hasVideo && videoUrl && !isOverlayActive && (
            <div className="capsule-video">
              <VideoPlayer
                ref={videoPlayerRef}
                src={videoUrl}
                zoomRegions={zoomRegions}
                onVideoReady={setVideoElement}
                onVolumeChange={setVideoVolume}
              />
            </div>
          )}

          {/* Audio controls (when NOT video mode) */}
          {!isIntroLayer && !hasVideo && audioUrl && (
            <CapsuleAudioControls
              audioPlaying={audioPlaying}
              audioReady={audioReady}
              onPlay={playAudio}
              onPause={pauseAudio}
              onStop={handleStopAudio}
              offset={offset}
            />
          )}

          {/* Notation viewer - stacked below video (non-overlay mode) */}
          {!isIntroLayer && showAlphaTab && !isOverlayActive && (
            <div
              ref={(el) => {
                // Get the AlphaTab notation container for scroll control
                if (el) {
                  const alphaTabContainer = el.querySelector('[data-testid="notation-container"]');
                  scoreContainerRef.current = alphaTabContainer as HTMLElement || el;
                }
              }}
              className={`capsule-score ${hasVideo ? 'capsule-score--video' : 'capsule-score--audio'}`}
            >
              {(scoreUrl || processedScoreData) && (
                <AlphaTabViewer
                  key={`${capsule.slug}-${selectedLayer}`}
                  scoreUrl={processedScoreData ? undefined : (scoreUrl || undefined)}
                  scoreData={processedScoreData || undefined}
                  onReady={handleAlphaTabReady}
                  onScoreLoaded={handleScoreLoaded}
                  hidePlaybackControls={!!(audioUrl || hasVideo)}
                  muteAudio={!!(audioUrl || hasVideo)}
                  layoutMode={hasVideo ? 'horizontal' : 'page'}
                />
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
